import { Empty, Tabs, TabsProps } from 'antd';
import MessangerLoader from 'components/Sceletons/MessangerLoader';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setDisplayChatDragger, setIsOpenDocsMenu, setIsOpenUsersMenu, setMessageForReply, setSelectedChatRoom } from 'store/slices/chatsSlice';
import Messanger from './Messanger';
import classes from './Messanger.module.scss';
import cn from 'classnames';
import StartDialogWithAI from './StartDialogWithAI/StartDialogWithAI';
import { TabPlacement } from 'antd/es/tabs';

type Props = {
  onSendMessage: (event: any, att: any) => void;
  embedded?: boolean;
  aiAssistant?: boolean;
};

const renderTabBar: TabsProps['renderTabBar'] = (props, DefaultTabBar) => {
  return <DefaultTabBar {...props} style={{ display: 'none' }} />;
};

export default function MessengerWithTabs({ onSendMessage, embedded, aiAssistant}: Props) {
  const [dragLeaveTimer, setDragLeaveTimer] = React.useState<NodeJS.Timeout>();

  const {
    roomAllChats, chatData, currnetChatLoading, selectedChat, globalChats, messageForReply, isOpenDocsMenu, isOpenUsersMenu
  } = useAppSelector((state) => state.chat);

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  React.useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedChat && !aiAssistant) {
        if (isOpenDocsMenu || isOpenUsersMenu) {
          dispatch(setIsOpenDocsMenu(false));
          dispatch(setIsOpenUsersMenu(false));
        }
        else if (messageForReply) dispatch(setMessageForReply(null));
        else if (selectedChat) dispatch(setSelectedChatRoom(null));
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [selectedChat, messageForReply, isOpenDocsMenu, isOpenUsersMenu]);

  const chatsList = React.useMemo(() => [...globalChats, ...roomAllChats], [globalChats, roomAllChats]);

  const items = chatsList.map((chat) => ({
    key: chat.id,
    label: <div>{chat.title}</div>,
    children:
      currnetChatLoading && selectedChat?.id === chat.id ? (
        <MessangerLoader />
      ) : (
        <Messanger
          onSendMessage={onSendMessage}
          chatGroups={chatData[chat.id]?.grouped}
          chatInfo={selectedChat}
          embedded={embedded}
          aiAssistant={aiAssistant}
        />
      ),
  }));

  const preventDefault = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDropEvent = (e: any) => {
    dispatch(setDisplayChatDragger(false));
    preventDefault(e);
  };
  
  const handleEnterEvent = (e: any) => {
    const types = e.dataTransfer?.types || [];
    const hasFiles =
      types.includes?.('Files') ||
      e.dataTransfer?.files?.length > 0 ||
      (types.length > 0 && String(types).toLowerCase().includes('file'));
  
    if (hasFiles) {
      clearInterval(dragLeaveTimer);
      dispatch(setDisplayChatDragger(true));
      preventDefault(e);
    }
  };
  
  const onDragOver = (e: any) => {
    clearInterval(dragLeaveTimer);
    preventDefault(e);
  };
  
  const onDragLeave = (e: any) => {
    clearInterval(dragLeaveTimer);
    setDragLeaveTimer(setTimeout(() => dispatch(setDisplayChatDragger(false)), 330));
    preventDefault(e);
  };

  return (
    chatsList.length && selectedChat
      ? (
        <Tabs
          classNames={{ popup: { root: classes.destroy } }}
          tabPlacement={'left' as TabPlacement}
          className={classes.messanger_tabs}
          items={items}
          renderTabBar={renderTabBar}
          activeKey={selectedChat?.id}
          moreIcon={null}
          onDrop={(e) => handleDropEvent(e)}
          onDragOver={(e) => onDragOver(e)}
          onDragEnter={(e) => handleEnterEvent(e)}
          onDragLeave={(e) => onDragLeave(e)}
        />
      ) : (
        <Empty
          description={ aiAssistant
            ? <StartDialogWithAI />
            : t('Chats.unselectedState')
          }
          className={cn(classes.empty, {[classes.aiEmpty]: aiAssistant})}
        />
      )
  );
}
